import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    ArrowLeft, Search, Trash2, LogOut, Copy, Loader2,
    Users, User, Archive as ArchiveIcon, ArchiveRestore, Boxes, Pencil, X,
    ChevronDown, Check, MoreHorizontal,
} from 'lucide-react';
import { useProjectStore, ACTIVE_PROJECT_CAP } from '../../stores/projectStore';
import { ProjectSummary, listSharedProjects, SharedProjectSummary, removeProjectMember } from '../../apiService';
import ConfirmDialog from '../ui/ConfirmDialog';
import { useToast } from '../ui/Toast';
import { formatCount } from '../../utils/plural';
import { useAuthActions } from '../../context/AuthContext';

/* ---------- Types local to this view ---------- */

type Scope = 'mine' | 'shared' | 'archived';
type TypeFilter = 'all' | 'research' | 'build';
type SortKey = 'updated_desc' | 'updated_asc' | 'name_asc' | 'name_desc';

const SORT_OPTIONS: Array<{ id: SortKey; label: string }> = [
    { id: 'updated_desc', label: 'Updated (newest first)' },
    { id: 'updated_asc',  label: 'Updated (oldest first)' },
    { id: 'name_asc',     label: 'Name (A → Z)' },
    { id: 'name_desc',    label: 'Name (Z → A)' },
];

function shortSortLabel(key: SortKey): React.ReactNode {
    // Arrow direction follows data-table convention: ↑ = ascending
    // (oldest → newest, A → Z), ↓ = descending (newest → oldest, Z → A).
    switch (key) {
        case 'updated_desc': return <>Updated <span aria-hidden="true">↓</span></>;
        case 'updated_asc':  return <>Updated <span aria-hidden="true">↑</span></>;
        case 'name_asc':     return <>Name <span aria-hidden="true">↑</span></>;
        case 'name_desc':    return <>Name <span aria-hidden="true">↓</span></>;
    }
}

interface ProjectLibraryViewProps {
    userId: string;
    onProjectSelect: (projectId: string) => void;
    onNewProject: (type: 'research' | 'build') => void;
    onBack?: () => void;
    onRenameProject?: (projectId: string, newName: string) => void;
    // Returns a per-call result so the library can detect partial failures in
    // batch deletes (Promise.all silently swallows them otherwise).
    onDeleteProject?: (projectId: string) => Promise<{ success: boolean; error?: string }>;
    // Move a project to the Archived FOLDER (status=archived_visible) / restore
    // it back to active. Distinct from delete (the hidden soft-delete).
    onArchiveProject?: (projectId: string) => Promise<{ success: boolean; error?: string }>;
    onRestoreProject?: (projectId: string) => Promise<{ success: boolean; error?: string }>;
    // Task 10 (nav-entry-project-list): when the view is opened from the
    // "Regulatory Research" / "Build & Artifact" left-nav entries we want to
    // land pre-filtered to that project type. Passing this prop preselects
    // the Type filter; the user can still widen to "All" if they want.
    initialTypeFilter?: TypeFilter;
}

interface Row {
    project_id: string;
    name: string;
    description: string;
    project_type?: 'research' | 'build' | string;
    status: string;
    created_at: string;
    updated_at: string;
    isShared: boolean;
    ownerLabel: string;
    // Shared-only: used for "am I the owner" check
    owner_id?: string;
    // Which list this row came from. Drives scope filtering — a recency-overflow
    // project keeps status='active' but belongs to the Archived bucket, so we
    // can't infer scope from status alone.
    bucket: 'active' | 'shared' | 'archived';
}

// One entry in a project row's overflow ("⋯") action menu.
interface RowAction {
    key: string;
    label: string;
    icon: React.ReactNode;
    onClick: () => void;
    danger?: boolean;
    // Render a divider above this item — used to set destructive actions
    // (Delete / Leave) apart from the safe ones.
    separatorBefore?: boolean;
}

/* ---------- Side-nav-matching glyphs ---------- */

// Use the EXACT same icons as the left-nav rail so the Project Library reads as
// the same app: Regulatory Research = magnifier, Build & Artifacts = beaker.
// (lucide has no exact match, so these mirror the rail's inline SVG paths.)
const ResearchGlyph = (props: any) => (
    <svg {...props} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
);
const BuildGlyph = (props: any) => (
    <svg {...props} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
    </svg>
);

/* ---------- Config ---------- */

// Owned views (the "Your projects" group): Recent = your 50 most-recent active
// projects; Archived = the recency overflow + anything you manually archived.
const OWNED_SCOPES: Array<{ id: Scope; label: string; icon: React.ComponentType<any> }> = [
    { id: 'mine',     label: 'Recent',   icon: User },
    { id: 'archived', label: 'Archived', icon: ArchiveIcon },
];
// Projects other people shared with you — a different axis (access, not yours),
// so its own group.
const SHARED_SCOPES: Array<{ id: Scope; label: string; icon: React.ComponentType<any> }> = [
    { id: 'shared',   label: 'Shared',   icon: Users },
];

// Derived from the scope arrays so labels can't drift out of sync with them.
const SCOPE_LABEL = Object.fromEntries(
    [...OWNED_SCOPES, ...SHARED_SCOPES].map(s => [s.id, s.label]),
) as Record<Scope, string>;

const TYPES: Array<{ id: TypeFilter; label: string; icon: React.ComponentType<any> }> = [
    { id: 'all',      label: 'All',      icon: Boxes },
    { id: 'research', label: 'Research', icon: ResearchGlyph },
    { id: 'build',    label: 'Build',    icon: BuildGlyph },
];

/* ---------- Derivation helpers ---------- */

function inScope(r: Row, s: Scope): boolean {
    switch (s) {
        // Scope follows the source bucket, not status: the Archived bucket holds
        // both manually-archived projects AND the recency overflow (which keep
        // status='active'), so status alone can't tell active from archived.
        case 'mine':     return r.bucket === 'active';
        case 'shared':   return r.bucket === 'shared';
        case 'archived': return r.bucket === 'archived';
    }
}

function inType(r: Row, t: TypeFilter): boolean {
    if (t === 'all') return true;
    return r.project_type === t;
}

function relativeTime(iso: string): string {
    const delta = Date.now() - new Date(iso).getTime();
    const s = Math.floor(delta / 1000);
    if (s < 60) return 'just now';
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 30) return `${d}d ago`;
    const mo = Math.floor(d / 30);
    if (mo < 12) return `${mo}mo ago`;
    return `${Math.floor(mo / 12)}y ago`;
}

/* ---------- Visual tokens ---------- */

const TYPE_STYLE: Record<string, { label: string; bg: string; text: string; icon: React.ComponentType<any> }> = {
    research: { label: 'RESEARCH', bg: 'bg-[color:var(--bark-teal-light)]',      text: 'text-[color:var(--bark-teal)]', icon: ResearchGlyph },
    build:    { label: 'BUILD',    bg: 'bg-accent-copper-tint',                  text: 'text-accent-copper',   icon: BuildGlyph },
};

function typeStyleFor(t?: string) {
    return t === 'build' ? TYPE_STYLE.build : TYPE_STYLE.research;
}

/* ---------- Component ---------- */

function ProjectLibraryView({ userId, onProjectSelect, onNewProject, onBack, onRenameProject, onDeleteProject, onArchiveProject, onRestoreProject, initialTypeFilter }: ProjectLibraryViewProps) {
    const { showToast } = useToast();
    const { projectList, listLoading, loadProjectList } = useProjectStore();
    const archivedProjectList = useProjectStore(s => s.archivedProjectList);
    const archivedLoaded = useProjectStore(s => s.archivedLoaded);
    const archivedLoading = useProjectStore(s => s.archivedLoading);
    const loadArchivedProjects = useProjectStore(s => s.loadArchivedProjects);
    const totalProjects = useProjectStore(s => s.totalProjects);
    const archivedVisibleCount = useProjectStore(s => s.archivedVisibleCount);
    const loadArchivedCount = useProjectStore(s => s.loadArchivedCount);
    const pendingRenameProjectId = useProjectStore(s => s.pendingRenameProjectId);
    const clearPendingRename = useProjectStore(s => s.clearPendingRename);

    const [sharedProjects, setSharedProjects] = useState<SharedProjectSummary[]>([]);
    const [sharedLoading, setSharedLoading] = useState(false);

    // An org without Build has no Build surface here either: no type tab, no
    // "New build project" CTA, and no build rows. Every other entry point
    // (sidebar, home doors, module cards) renders one shared Locked state that
    // explains the gate; the library repeating it would be a second
    // explanation, and leaving the rows clickable would be a dead end — the
    // /build/:id route bounces them straight back (ShellRoutes ModuleGate).
    const { isModuleDisabled } = useAuthActions();
    const buildLocked = isModuleDisabled('build_artifact');

    // `?type=build` is reachable by hand-typed URL, so a locked org lands on
    // "All" rather than on a tab that no longer exists — which would otherwise
    // filter every row away and show them an empty library.
    const effectiveInitialType: TypeFilter =
        buildLocked && initialTypeFilter === 'build' ? 'all' : (initialTypeFilter ?? 'all');

    const [activeScope, setActiveScope] = useState<Scope>('mine');
    const [activeType, setActiveType] = useState<TypeFilter>(effectiveInitialType);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortKey, setSortKey] = useState<SortKey>('updated_desc');
    const [sortOpen, setSortOpen] = useState(false);
    const sortMenuRef = useRef<HTMLDivElement | null>(null);
    const sortTriggerRef = useRef<HTMLButtonElement | null>(null);
    const sortItemRefs = useRef<Array<HTMLButtonElement | null>>([]);
    const sortMenuId = React.useId();

    // Close the sort dropdown on outside click or Escape. ARIA menu pattern:
    // Escape returns focus to the trigger so keyboard users land back where
    // they started.
    useEffect(() => {
        if (!sortOpen) return;
        const onPointerDown = (e: PointerEvent) => {
            if (!sortMenuRef.current?.contains(e.target as Node)) {
                setSortOpen(false);
            }
        };
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                setSortOpen(false);
                sortTriggerRef.current?.focus();
            }
        };
        document.addEventListener('pointerdown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('pointerdown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [sortOpen]);

    // When the sort menu opens, move focus to the currently-selected option so
    // arrow keys / Enter immediately operate on it.
    useEffect(() => {
        if (!sortOpen) return;
        const idx = Math.max(0, SORT_OPTIONS.findIndex(o => o.id === sortKey));
        sortItemRefs.current[idx]?.focus();
    }, [sortOpen, sortKey]);

    const [isActing, setIsActing] = useState(false);

    // Multi-select state. `selectedIds` only holds rows the user could batch-act
    // on (owned, non-shared); shared rows are intentionally not selectable since
    // batch delete only works on owned projects. `pendingDelete` carries the
    // rows being confirmed in the ConfirmDialog — a single Row for the per-row
    // trash icon, or an array of Rows for the floating-bar batch action.
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [pendingDelete, setPendingDelete] = useState<Row[] | null>(null);
    const [deleteSubmitting, setDeleteSubmitting] = useState(false);
    // Separate confirmation surface for the shared-project Leave action. The
    // owner path is destructive ("remove for ALL members"); the member path
    // just drops the caller's access. Both are different enough from the
    // owned-project delete flow that they get their own dialog state.
    const [pendingLeave, setPendingLeave] = useState<{ row: Row; isOwner: boolean } | null>(null);
    const [leaveSubmitting, setLeaveSubmitting] = useState(false);

    // Inline rename state. Only one project can be in rename mode at a time.
    const [renamingProjectId, setRenamingProjectId] = useState<string | null>(null);
    const [renameDraft, setRenameDraft] = useState('');
    const [renameSubmitting, setRenameSubmitting] = useState(false);
    // Re-entry guard for commitRename: blur and Enter can both fire in the same
    // tick when committing — the ref keeps state consistent across both paths.
    const renameCommitInFlightRef = useRef(false);

    // If the caller remounts the view with a different initial type filter
    // (e.g. switching between "Regulatory Research" and "Build & Artifact"
    // left-nav entries), reflect that in the active Type chip.
    useEffect(() => {
        if (initialTypeFilter) setActiveType(effectiveInitialType);
    }, [initialTypeFilter, effectiveInitialType]);

    useEffect(() => {
        if (!userId) return;
        loadProjectList(userId, 1, false, ACTIVE_PROJECT_CAP);
        // Cheap count so the Archived badge shows a number on load; the full
        // Archived list still loads lazily when the tab is opened.
        loadArchivedCount(userId);
        setSharedLoading(true);
        listSharedProjects(userId).then(result => {
            if (result.success && result.projects) setSharedProjects(result.projects);
            setSharedLoading(false);
        });
    }, [userId, loadProjectList, loadArchivedCount]);

    /* ---------- Flatten both lists into a single Row[] ---------- */

    const rows: Row[] = useMemo(() => {
        const me = userId.split('@')[0] || 'me';
        // Drop build projects entirely for a locked org — including ones shared
        // with them — so scope counts, batch-select and the empty state all
        // agree with what is actually reachable.
        const visible = <T extends { project_type?: string }>(list: T[]) =>
            (buildLocked ? list.filter(p => p.project_type !== 'build') : list);
        const activeIds = new Set(projectList.map(p => p.project_id));
        const mine: Row[] = visible(projectList).map((p: ProjectSummary) => ({
            project_id: p.project_id,
            name: p.name,
            description: p.description,
            project_type: p.project_type,
            status: p.status,
            created_at: p.created_at,
            updated_at: p.updated_at,
            isShared: false,
            ownerLabel: me,
            bucket: 'active' as const,
        }));
        const shared: Row[] = visible(sharedProjects).map((p: SharedProjectSummary) => ({
            project_id: p.project_id,
            name: p.name,
            description: p.description,
            project_type: p.project_type,
            status: p.status,
            created_at: p.created_at,
            updated_at: p.updated_at,
            isShared: true,
            ownerLabel: p.owner_name || p.owner_email || 'unknown',
            owner_id: p.owner_id,
            bucket: 'shared' as const,
        }));
        // Archived bucket = manually-archived (archived_visible) + recency
        // overflow (active beyond the top ACTIVE_PROJECT_CAP), fetched
        // separately. Dedup against the active 50 in case a late updated_at
        // shifted ranking between the two fetches.
        const archived: Row[] = visible(archivedProjectList)
            .filter((p) => !activeIds.has(p.project_id))
            .map((p: ProjectSummary) => ({
                project_id: p.project_id,
                name: p.name,
                description: p.description,
                project_type: p.project_type,
                status: p.status,
                created_at: p.created_at,
                updated_at: p.updated_at,
                isShared: false,
                ownerLabel: me,
                bucket: 'archived' as const,
            }));
        return [...mine, ...shared, ...archived];
    }, [projectList, sharedProjects, archivedProjectList, userId, buildLocked]);

    // With no Build, "All" and "Research" describe the same set, so the Type
    // group collapses to nothing rather than offering a one-item choice.
    const visibleTypes = useMemo(
        () => (buildLocked ? [] : TYPES),
        [buildLocked],
    );

    // Lazy-load the Archived folder when the user opens that tab — but only if
    // the cache is stale (archivedLoaded is reset whenever the active list
    // reloads). Reusing the cache makes repeat opens instant.
    useEffect(() => {
        if (activeScope === 'archived' && userId && !archivedLoaded) loadArchivedProjects(userId);
    }, [activeScope, userId, archivedLoaded, loadArchivedProjects]);

    /* ---------- Filter + sort ---------- */

    const visibleRows = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        // Pre-compute Date.getTime() once per row instead of re-parsing inside
        // every comparator call (O(n log n) Date allocations otherwise).
        const decorated = rows
            .filter(r =>
                inScope(r, activeScope) &&
                inType(r, activeType) &&
                (q === '' || r.name.toLowerCase().includes(q))
            )
            .map(r => ({ r, ms: new Date(r.updated_at).getTime() }));
        decorated.sort((a, b) => {
            switch (sortKey) {
                case 'updated_desc': return b.ms - a.ms;
                case 'updated_asc':  return a.ms - b.ms;
                case 'name_asc':     return a.r.name.localeCompare(b.r.name);
                case 'name_desc':    return b.r.name.localeCompare(a.r.name);
                // Safety: if SortKey ever gains a case the switch hasn't been
                // updated for, fall back to "no reordering" instead of letting
                // Array.sort see undefined.
                default: return 0;
            }
        });
        return decorated.map(d => d.r);
    }, [rows, activeScope, activeType, searchQuery, sortKey]);

    const scopeCounts = useMemo(() => ({
        mine:     rows.filter(r => r.bucket === 'active').length,
        shared:   rows.filter(r => r.bucket === 'shared').length,
        // Before the Archived tab has loaded its list, the rows have no archived
        // entries — so estimate from the eager count: manually-archived projects
        // plus the recency overflow (active beyond the cap). Once the tab loads,
        // use the real deduped list count so the badge matches what's shown.
        archived: archivedLoaded
            ? rows.filter(r => r.bucket === 'archived').length
            : archivedVisibleCount + Math.max(0, totalProjects - ACTIVE_PROJECT_CAP),
    }), [rows, archivedLoaded, archivedVisibleCount, totalProjects]);

    /* ---------- Actions ---------- */

    const startRename = (projectId: string, currentName: string) => {
        setRenamingProjectId(projectId);
        setRenameDraft(currentName);
    };

    const cancelRename = () => {
        setRenamingProjectId(null);
        setRenameDraft('');
        setRenameSubmitting(false);
    };

    const commitRename = async () => {
        if (renameCommitInFlightRef.current) return;
        if (!onRenameProject || !renamingProjectId) {
            cancelRename();
            return;
        }
        const trimmed = renameDraft.trim();
        const original = rows.find(r => r.project_id === renamingProjectId)?.name ?? '';
        if (!trimmed || trimmed === original) {
            cancelRename();
            return;
        }
        renameCommitInFlightRef.current = true;
        setRenameSubmitting(true);
        try {
            await onRenameProject(renamingProjectId, trimmed);
        } finally {
            renameCommitInFlightRef.current = false;
            cancelRename();
        }
    };

    // After a duplicate completes, the store sets pendingRenameProjectId so the
    // user can immediately name the new copy. Auto-enter inline rename mode for
    // that row when it shows up in the list.
    useEffect(() => {
        if (!pendingRenameProjectId) return;
        const row = rows.find(r => r.project_id === pendingRenameProjectId);
        if (!row) return;
        setRenamingProjectId(pendingRenameProjectId);
        setRenameDraft(row.name);
        clearPendingRename();
    }, [pendingRenameProjectId, rows, clearPendingRename]);

    const handleRename = (r: Row) => {
        if (r.isShared) {
            showToast({ variant: 'warning', message: 'Only the owner can rename a project.' });
            return;
        }
        startRename(r.project_id, r.name);
    };

    const handleDelete = (r: Row) => {
        if (!onDeleteProject || r.isShared) return;
        setPendingDelete([r]);
    };

    const handleArchive = async (r: Row) => {
        if (!onArchiveProject || r.isShared) return;
        setIsActing(true);
        try {
            const res = await onArchiveProject(r.project_id);
            if (res.success) loadProjectList(userId, 1, false, ACTIVE_PROJECT_CAP);   // it left the active list
            else showToast({ variant: 'error', message: `Failed to archive project: ${res.error}` });
        } finally {
            setIsActing(false);
        }
    };

    const handleRestore = async (r: Row) => {
        if (!onRestoreProject) return;
        setIsActing(true);
        try {
            const res = await onRestoreProject(r.project_id);
            // unshelveProject already removed it from the archived list optimistically.
            // loadProjectList refreshes "Mine" AND invalidates the archived cache, so
            // the effect refetches the folder once (correct overflow) — no manual call.
            if (res.success) loadProjectList(userId, 1, false, ACTIVE_PROJECT_CAP);
            else showToast({ variant: 'error', message: `Failed to restore project: ${res.error}` });
        } finally {
            setIsActing(false);
        }
    };

    const handleBatchDelete = () => {
        if (!onDeleteProject || selectedIds.size === 0) return;
        const targets = rows.filter(r => selectedIds.has(r.project_id) && !r.isShared);
        if (targets.length === 0) return;
        setPendingDelete(targets);
    };

    const confirmDelete = async () => {
        if (!onDeleteProject || !pendingDelete) return;
        setDeleteSubmitting(true);
        setIsActing(true);
        try {
            // Promise.allSettled (not all) so we can detect per-item failures.
            // The previous Promise.all swallowed partial failures because the
            // App.tsx onDeleteProject callback caught its own errors.
            const results = await Promise.allSettled(
                pendingDelete.map(r => onDeleteProject(r.project_id))
            );
            const failedRows: Row[] = [];
            results.forEach((res, i) => {
                const row = pendingDelete[i];
                if (res.status === 'rejected' || !res.value.success) {
                    failedRows.push(row);
                }
            });
            loadProjectList(userId, 1, false, ACTIVE_PROJECT_CAP);

            if (failedRows.length > 0) {
                const msg =
                    pendingDelete.length === 1
                        ? `Failed to delete "${pendingDelete[0].name}".`
                        : `Failed to delete ${failedRows.length} of ${formatCount(
                            pendingDelete.length,
                            { one: 'project', other: 'projects' },
                        )}.`;
                showToast({ variant: 'error', message: msg });
                // For batch deletes (>1 originally targeted), collapse the
                // selection down to just the failures so the user can retry.
                // For single-row trash-icon deletes the user wasn't in
                // selection mode, so leave selectedIds alone.
                if (pendingDelete.length > 1) {
                    setSelectedIds(new Set(failedRows.map(r => r.project_id)));
                }
            } else {
                // All succeeded — remove the deleted rows from any existing
                // selection (the batch path will clear everything; the single-
                // row path is a no-op when selectedIds was already empty).
                setSelectedIds(prev => {
                    const next = new Set(prev);
                    pendingDelete.forEach(r => next.delete(r.project_id));
                    return next;
                });
            }
        } finally {
            setDeleteSubmitting(false);
            setIsActing(false);
            setPendingDelete(null);
        }
    };

    const toggleSelect = (projectId: string) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(projectId)) next.delete(projectId);
            else next.add(projectId);
            return next;
        });
    };

    const clearSelection = () => setSelectedIds(new Set());

    // Drop selection IDs that no longer exist in the row set — e.g. if a
    // background reload (websocket, another tab) removed them. Without this,
    // the floating-bar count would be wrong and batch-delete would silently
    // skip the missing rows.
    useEffect(() => {
        setSelectedIds(prev => {
            if (prev.size === 0) return prev;
            const validIds = new Set(rows.map(r => r.project_id));
            const next = new Set(Array.from(prev).filter(id => validIds.has(id)));
            return next.size === prev.size ? prev : next;
        });
    }, [rows]);

    const duplicateProjectAction = useProjectStore(s => (s as any).duplicateProject);
    const duplicateJobs = useProjectStore(s => (s as any).duplicateJobs) as
        | { [k: string]: { jobId: string; newProjectId?: string; phase?: string } }
        | undefined;

    const handleDuplicate = async (r: Row) => {
        if (r.isShared) {
            showToast({ variant: 'warning', message: 'Only the owner can duplicate a project.' });
            return;
        }
        setIsActing(true);
        try {
            const result = await duplicateProjectAction(r.project_id, userId);
            if (!result.success) {
                showToast({
                    variant: 'error',
                    message: `Duplicate failed: ${result.error || 'Unknown error'}`,
                });
            } else {
                if (result.warnings && Object.keys(result.warnings).length > 0) {
                    const files = Object.keys(result.warnings).join(', ');
                    showToast({
                        variant: 'warning',
                        durationMs: 0,
                        message:
                            'Project duplicated. Some notebooks reference paths from the original project ' +
                            `and need manual edits: ${files}`,
                    });
                }
            }
        } finally {
            setIsActing(false);
        }
    };

    const handleLeave = (r: Row) => {
        if (!r.isShared) return;
        setPendingLeave({ row: r, isOwner: r.owner_id === userId });
    };

    const confirmLeave = async () => {
        if (!pendingLeave) return;
        const { row, isOwner } = pendingLeave;
        setLeaveSubmitting(true);
        setIsActing(true);
        try {
            if (isOwner) {
                if (!onDeleteProject) return;
                const result = await onDeleteProject(row.project_id);
                if (!result.success) {
                    showToast({
                        variant: 'error',
                        message: `Failed to delete "${row.name}": ${result.error ?? 'Unknown error'}`,
                    });
                    return;
                }
            } else {
                const result = await removeProjectMember(row.project_id, userId, userId);
                if (!result.success) {
                    showToast({
                        variant: 'error',
                        message: `Failed to leave "${row.name}": ${result.error ?? 'Unknown error'}`,
                    });
                    return;
                }
            }
            const shared = await listSharedProjects(userId);
            if (shared.success && shared.projects) setSharedProjects(shared.projects);
        } finally {
            setLeaveSubmitting(false);
            setIsActing(false);
            setPendingLeave(null);
        }
    };

    // Changing scope/type cancels any in-progress inline rename since the
    // renaming row may filter out of view. Selections also clear because the
    // selected rows may belong to a different filtered list — a user clicking
    // Delete after a scope change should never be acting on hidden rows.
    const changeScope = (s: Scope)       => { setActiveScope(s); cancelRename(); clearSelection(); };
    const changeType  = (t: TypeFilter)  => { setActiveType(t);  cancelRename(); clearSelection(); };

    /* ---------- Render ---------- */

    return (
        <div className="h-full flex bg-bg">
            {/* MAIN — single workspace. The bordered filter sidebar is gone; the
                filters now live as a plain-text column inside the body, and the
                whole [filters | list] block is centered in the content area. */}
            <main className="flex-1 flex flex-col overflow-hidden relative">
                {/* No left padding here: the filter gutter must start at the true
                    content-left (right after the icon rail) so the filters sit
                    symmetrically — equal gap to the rail and to the table. pr-8
                    keeps the table off the right edge. */}
                <div className="flex-1 overflow-y-auto pr-8 pt-6 pb-8">
                    <div className="flex items-start">
                        {/* Left gutter: the "Project Library" title shares the row
                            with the search bar, and the plain-text filters sit
                            below it. pl-[42px] keeps the symmetric gap. */}
                        <div className="w-[260px] flex-shrink-0 pl-[42px]">
                            {/* h-[34px] mirrors the search input so the title lines
                                up with the search bar, and the filters below line up
                                with the top of the list. */}
                            <div className="flex items-center gap-2 mb-6 h-[34px]">
                                {onBack && (
                                    <button
                                        onClick={onBack}
                                        className="p-1 -ml-1 rounded-md text-fg-tertiary hover:bg-bg-warm hover:text-fg transition-colors"
                                        aria-label="Back"
                                    >
                                        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
                                    </button>
                                )}
                                <h1 className="text-[20px] font-semibold tracking-[-0.01em] text-fg whitespace-nowrap">Project Library</h1>
                            </div>
                            <nav className="w-[176px] space-y-5">
                                <SidebarSection
                                    eyebrow="Your projects"
                                    items={OWNED_SCOPES.map(s => ({ ...s, count: scopeCounts[s.id], active: activeScope === s.id, onClick: () => changeScope(s.id) }))}
                                />
                                <SidebarSection
                                    eyebrow="Shared with you"
                                    items={SHARED_SCOPES.map(s => ({ ...s, count: scopeCounts[s.id], active: activeScope === s.id, onClick: () => changeScope(s.id) }))}
                                />
                                {visibleTypes.length > 0 && (
                                    <SidebarSection
                                        eyebrow="Type"
                                        items={visibleTypes.map(t => ({ ...t, count: undefined, active: activeType === t.id, onClick: () => changeType(t.id) }))}
                                    />
                                )}
                            </nav>
                        </div>

                        <div className="flex-1 min-w-0 max-w-[920px]">
                            <div className="flex items-center gap-3 mb-6">
                        {/* Search on the left, filling to the table's right edge;
                            Sort sits at the right. Search is always rendered (even
                            at zero results) so the query stays clearable. */}
                        <div className="relative flex-1">
                            <Search
                                className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-fg-tertiary"
                                strokeWidth={1.5}
                            />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search projects…"
                                className="w-full pl-8 pr-3 py-1.5 text-sm bg-surface border border-border-default rounded-md text-fg placeholder-fg-tertiary focus:outline-none focus:border-accent focus:shadow-bark-focus transition-shadow"
                            />
                        </div>
                        {visibleRows.length > 0 && (
                            <div ref={sortMenuRef} className="relative">
                                <button
                                    ref={sortTriggerRef}
                                    type="button"
                                    onClick={() => setSortOpen(o => !o)}
                                    aria-haspopup="menu"
                                    aria-expanded={sortOpen}
                                    aria-controls={sortMenuId}
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[12px] text-fg-secondary hover:bg-bg-warm focus:outline-none focus-visible:shadow-bark-focus transition-colors whitespace-nowrap"
                                >
                                    <span className="text-fg-tertiary">Sort:</span>
                                    <span className="font-medium text-fg">{shortSortLabel(sortKey)}</span>
                                    <ChevronDown
                                        className={`w-3.5 h-3.5 text-fg-tertiary transition-transform ${sortOpen ? 'rotate-180' : ''}`}
                                        strokeWidth={1.8}
                                    />
                                </button>
                                {sortOpen && (
                                    <div
                                        id={sortMenuId}
                                        role="menu"
                                        aria-orientation="vertical"
                                        onKeyDown={(e) => {
                                            // Arrow keys move focus between options; Tab closes the menu
                                            // (ARIA spec — focus then leaves the menu naturally).
                                            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                                                e.preventDefault();
                                                const currentIdx = sortItemRefs.current.findIndex(b => b === document.activeElement);
                                                const len = SORT_OPTIONS.length;
                                                const start = currentIdx >= 0 ? currentIdx : 0;
                                                const next = e.key === 'ArrowDown'
                                                    ? (start + 1) % len
                                                    : (start - 1 + len) % len;
                                                sortItemRefs.current[next]?.focus();
                                            } else if (e.key === 'Home') {
                                                e.preventDefault();
                                                sortItemRefs.current[0]?.focus();
                                            } else if (e.key === 'End') {
                                                e.preventDefault();
                                                sortItemRefs.current[SORT_OPTIONS.length - 1]?.focus();
                                            } else if (e.key === 'Tab') {
                                                setSortOpen(false);
                                            }
                                        }}
                                        className="absolute left-0 top-full mt-1 min-w-[220px] bg-surface border border-border-default rounded-md shadow-bark-md py-1 z-30"
                                    >
                                        {SORT_OPTIONS.map((opt, i) => (
                                            <button
                                                key={opt.id}
                                                ref={(el) => { sortItemRefs.current[i] = el; }}
                                                type="button"
                                                role="menuitemradio"
                                                aria-checked={sortKey === opt.id}
                                                onClick={() => {
                                                    setSortKey(opt.id);
                                                    setSortOpen(false);
                                                    sortTriggerRef.current?.focus();
                                                }}
                                                className={`w-full flex items-center gap-2 px-3 py-1.5 text-[13px] text-left transition-colors focus:outline-none focus:bg-bg-warm ${
                                                    sortKey === opt.id
                                                        ? 'text-fg font-medium'
                                                        : 'text-fg-secondary hover:bg-bg-warm hover:text-fg'
                                                }`}
                                            >
                                                <Check
                                                    className={`w-3.5 h-3.5 shrink-0 ${sortKey === opt.id ? 'text-[color:var(--primary)]' : 'text-transparent'}`}
                                                    strokeWidth={2.2}
                                                />
                                                {opt.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    {/* List */}
                    {listLoading && rows.length === 0 ? (
                        <div className="text-sm text-fg-tertiary py-8">Loading projects…</div>
                    ) : sharedLoading && rows.length === 0 ? (
                        <div className="text-sm text-fg-tertiary py-8">Loading shared projects…</div>
                    ) : activeScope === 'archived' && archivedLoading && visibleRows.length === 0 ? (
                        <div className="flex items-center gap-2 text-sm text-fg-tertiary py-8">
                            <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                            Loading archived projects…
                        </div>
                    ) : visibleRows.length === 0 ? (
                        <EmptyState
                            onNewProject={onNewProject}
                            scope={activeScope}
                            hasAnyProjects={rows.length > 0}
                            buildLocked={buildLocked}
                        />
                    ) : (
                        <div className="border border-border-default rounded-lg bg-surface overflow-hidden w-full">
                            {visibleRows.map((r) => {
                                const isRenaming = renamingProjectId === r.project_id;
                                // True only for MANUALLY archived projects (status
                                // 'archived_visible') — not recency-overflow rows,
                                // which keep status='active' and get normal actions.
                                const isManuallyArchived = r.status === 'archived_visible';
                                // Manually-archived rows offer only Restore (+ Delete);
                                // hide rename/duplicate/archive on them.
                                const canRename = !r.isShared && !!onRenameProject && !isManuallyArchived;
                                const canSelect = !r.isShared && !!onDeleteProject;
                                return (
                                    <ProjectRow
                                        key={r.project_id}
                                        row={r}
                                        onOpen={() => onProjectSelect(r.project_id)}
                                        isRenaming={isRenaming}
                                        renameDraft={renameDraft}
                                        renameSubmitting={renameSubmitting}
                                        canRename={canRename}
                                        canDuplicate={!r.isShared && !isManuallyArchived}
                                        canDelete={!r.isShared && !!onDeleteProject}
                                        canArchive={!r.isShared && !!onArchiveProject && !isManuallyArchived}
                                        canRestore={!r.isShared && !!onRestoreProject && isManuallyArchived}
                                        canLeave={r.isShared}
                                        isActing={isActing}
                                        duplicatePhase={duplicateJobs?.[r.project_id]?.phase}
                                        isSharedOwner={r.owner_id === userId}
                                        canSelect={canSelect}
                                        isSelected={selectedIds.has(r.project_id)}
                                        onToggleSelect={() => toggleSelect(r.project_id)}
                                        onRename={() => handleRename(r)}
                                        onDuplicate={() => handleDuplicate(r)}
                                        onDelete={() => handleDelete(r)}
                                        onArchive={() => handleArchive(r)}
                                        onRestore={() => handleRestore(r)}
                                        onLeave={() => handleLeave(r)}
                                        onRenameDraftChange={setRenameDraft}
                                        onRenameCommit={commitRename}
                                        onRenameCancel={cancelRename}
                                    />
                                );
                            })}
                        </div>
                    )}
                        </div>
                    </div>
                </div>

                {/* Selection toolbar — absolute inside <main> (which is relative)
                    so it centers on the content area / table, not the whole window
                    (the left sidebars would otherwise pull it off-centre). */}
                {selectedIds.size > 0 && (
                    <div
                        role="toolbar"
                        aria-label={`${selectedIds.size} selected`}
                        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-40
                                   flex items-center gap-3 px-4 py-2.5
                                   bg-surface border border-border-default rounded-xl shadow-bark-lg"
                    >
                        <span className="text-[13px] font-medium text-fg">
                            {selectedIds.size} selected
                        </span>
                        <span className="h-4 w-px bg-divider" aria-hidden="true" />
                        <button
                            type="button"
                            onClick={handleBatchDelete}
                            disabled={isActing}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[13px] font-medium rounded-md text-danger hover:bg-danger-bg focus:outline-none focus-visible:shadow-bark-focus transition-colors disabled:opacity-60"
                        >
                            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.8} />
                            Delete
                        </button>
                        <button
                            type="button"
                            onClick={clearSelection}
                            className="inline-flex items-center gap-1.5 px-2 py-1 text-[13px] font-medium rounded-md text-fg-secondary hover:bg-bg-warm focus:outline-none focus-visible:shadow-bark-focus transition-colors"
                            aria-label="Clear selection"
                        >
                            <X className="w-3.5 h-3.5" strokeWidth={1.8} />
                            Clear
                        </button>
                    </div>
                )}

            </main>

            <ConfirmDialog
                open={pendingDelete !== null}
                onCancel={() => {
                    if (!deleteSubmitting) setPendingDelete(null);
                }}
                onConfirm={confirmDelete}
                title={
                    pendingDelete && pendingDelete.length === 1
                        ? `Delete "${pendingDelete[0].name}"?`
                        : pendingDelete
                            ? `Delete ${formatCount(pendingDelete.length, { one: 'project', other: 'projects' })}?`
                            : ''
                }
                description="This cannot be undone."
                items={
                    pendingDelete && pendingDelete.length > 1
                        ? pendingDelete.map(r => r.name)
                        : undefined
                }
                confirmLabel="Delete"
                variant="destructive"
                submitting={deleteSubmitting}
            />

            <ConfirmDialog
                open={pendingLeave !== null}
                onCancel={() => {
                    if (!leaveSubmitting) setPendingLeave(null);
                }}
                onConfirm={confirmLeave}
                title={
                    pendingLeave
                        ? pendingLeave.isOwner
                            ? `Delete "${pendingLeave.row.name}" for everyone?`
                            : `Leave "${pendingLeave.row.name}"?`
                        : ''
                }
                description={
                    pendingLeave?.isOwner
                        ? 'You own this project. Deleting will remove it for all members.'
                        : 'You will lose access to this project.'
                }
                confirmLabel={pendingLeave?.isOwner ? 'Delete' : 'Leave'}
                variant="destructive"
                submitting={leaveSubmitting}
            />
        </div>
    );
}

/* ---------- Sub-components ---------- */

interface SidebarItem {
    id: string;
    label: string;
    icon: React.ComponentType<any>;
    count?: number;
    active: boolean;
    onClick: () => void;
}

function SidebarSection({ eyebrow, items }: { eyebrow: string; items: SidebarItem[] }) {
    return (
        <div>
            <div className="px-2 font-body text-[10px] font-bold uppercase tracking-[0.08em] text-fg-tertiary mb-1.5">
                {eyebrow}
            </div>
            <div className="flex flex-col">
                {items.map((it) => {
                    const Icon = it.icon;
                    return (
                        <button
                            key={it.id}
                            onClick={it.onClick}
                            className={`flex items-center gap-2.5 px-2 py-1.5 -mx-2 rounded-md text-sm transition-colors ${
                                it.active
                                    ? 'bg-accent-tint text-accent-hover font-semibold'
                                    : 'text-fg-secondary hover:bg-bg-warm hover:text-fg'
                            }`}
                        >
                            <Icon className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={1.5} />
                            <span className="flex-1 text-left">{it.label}</span>
                            {it.count !== undefined && (
                                <span className={`font-mono text-[10px] tabular-nums ${
                                    it.active ? 'text-accent-hover' : 'text-fg-tertiary'
                                }`}>
                                    {it.count}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

function ProjectRow({
    row, onOpen,
    isRenaming, renameDraft, renameSubmitting, canRename,
    canDuplicate, canDelete, canArchive, canRestore, canLeave, isActing, duplicatePhase, isSharedOwner,
    canSelect, isSelected, onToggleSelect,
    onRename, onDuplicate, onDelete, onArchive, onRestore, onLeave,
    onRenameDraftChange, onRenameCommit, onRenameCancel,
}: {
    row: Row;
    onOpen: () => void;
    isRenaming: boolean;
    renameDraft: string;
    renameSubmitting: boolean;
    canRename: boolean;
    canDuplicate: boolean;
    canDelete: boolean;
    canArchive: boolean;
    canRestore: boolean;
    canLeave: boolean;
    isActing: boolean;
    duplicatePhase?: string;
    isSharedOwner: boolean;
    canSelect: boolean;
    isSelected: boolean;
    onToggleSelect: () => void;
    onRename: () => void;
    onDuplicate: () => void;
    onDelete: () => void;
    onArchive: () => void;
    onRestore: () => void;
    onLeave: () => void;
    onRenameDraftChange: (next: string) => void;
    onRenameCommit: () => void;
    onRenameCancel: () => void;
}) {
    const ts = typeStyleFor(row.project_type);
    const TypeIcon = ts.icon;

    const inputRef = useRef<HTMLInputElement | null>(null);
    useEffect(() => {
        if (isRenaming) {
            const el = inputRef.current;
            if (el) {
                el.focus();
                el.select();
            }
        }
    }, [isRenaming]);

    // Row actions collapse into a single "⋯" menu (revealed on hover) instead
    // of a row of always-visible icons. Destructive actions (Delete / Leave)
    // are set apart with a divider so they aren't a one-click slip.
    const rowActions: RowAction[] = [
        canRename && {
            key: 'rename', label: 'Rename', onClick: onRename,
            icon: <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />,
        },
        canDuplicate && {
            key: 'duplicate',
            label: isActing && duplicatePhase ? `Duplicating (${duplicatePhase})…` : 'Duplicate',
            onClick: onDuplicate,
            icon: isActing && duplicatePhase
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} />
                : <Copy className="w-3.5 h-3.5" strokeWidth={1.5} />,
        },
        canRestore && {
            key: 'restore', label: isActing ? 'Restoring…' : 'Restore', onClick: onRestore,
            icon: <ArchiveRestore className="w-3.5 h-3.5" strokeWidth={1.5} />,
        },
        canArchive && {
            key: 'archive', label: isActing ? 'Archiving…' : 'Archive', onClick: onArchive,
            icon: <ArchiveIcon className="w-3.5 h-3.5" strokeWidth={1.5} />,
        },
        canDelete && {
            key: 'delete', label: isActing ? 'Deleting…' : 'Delete', onClick: onDelete,
            icon: <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />,
            danger: true, separatorBefore: true,
        },
        canLeave && {
            key: 'leave',
            label: isSharedOwner ? (isActing ? 'Deleting…' : 'Delete') : (isActing ? 'Leaving…' : 'Leave'),
            onClick: onLeave,
            icon: isSharedOwner
                ? <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                : <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} />,
            danger: isSharedOwner, separatorBefore: true,
        },
    ].filter(Boolean) as RowAction[];

    return (
        <div
            data-project-id={row.project_id}
            data-project-row="true"
            onClick={isRenaming ? undefined : onOpen}
            className={`group grid items-center gap-4 px-4 py-2.5 border-b last:border-b-0 border-divider transition-colors ${
                isRenaming ? '' : 'cursor-pointer'
            } ${
                isSelected
                    ? 'bg-[color:var(--primary)]/5'
                    : isRenaming
                        ? 'bg-surface'
                        : 'hover:bg-bg-warm'
            }`}
            style={{ gridTemplateColumns: '20px 28px minmax(0,1fr) auto 36px' }}
        >
            {canSelect ? (
                <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={onToggleSelect}
                    onClick={(e) => e.stopPropagation()}
                    aria-label={`Select ${row.name}`}
                    className={`w-4 h-4 accent-[color:var(--primary)] cursor-pointer transition-opacity ${
                        isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100'
                    }`}
                />
            ) : (
                <span aria-hidden="true" className="block w-4 h-4" />
            )}

            <div className={`w-7 h-7 rounded-md flex items-center justify-center ${ts.bg}`}>
                <TypeIcon className={`w-3.5 h-3.5 ${ts.text}`} strokeWidth={1.5} />
            </div>

            <div className="min-w-0">
                <div className="flex items-center gap-2">
                    {isRenaming ? (
                        <input
                            ref={inputRef}
                            type="text"
                            value={renameDraft}
                            onChange={(e) => onRenameDraftChange(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => {
                                e.stopPropagation();
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    onRenameCommit();
                                } else if (e.key === 'Escape') {
                                    e.preventDefault();
                                    onRenameCancel();
                                }
                            }}
                            onBlur={onRenameCommit}
                            disabled={renameSubmitting}
                            data-testid="project-rename-input"
                            aria-label="Project name"
                            className="flex-1 min-w-0 px-2 py-1 text-sm font-semibold bg-surface-sunken border border-border-default rounded-md text-fg focus:outline-none focus:border-accent focus:shadow-bark-focus transition-shadow disabled:opacity-60"
                        />
                    ) : (
                        <span className="text-sm font-semibold text-fg truncate">{row.name}</span>
                    )}
                    <span className={`font-body text-[10px] font-bold uppercase tracking-[0.08em] ${ts.text} flex-shrink-0`}>
                        {ts.label}
                    </span>
                </div>
                {/* Owner line only when it adds information — in your own
                    projects every row is "owned by you", which is just noise. */}
                {row.isShared && (
                    <div className="flex items-center gap-2 mt-0.5 font-mono text-[11px] text-fg-tertiary">
                        <span>{`shared by ${row.ownerLabel}`}</span>
                    </div>
                )}
            </div>

            <div className="font-mono text-[11px] text-fg-tertiary tabular-nums min-w-[72px] text-right">
                {relativeTime(row.updated_at)}
            </div>

            <div className="flex items-center justify-end">
                {!isRenaming && rowActions.length > 0 && (
                    <RowActionsMenu actions={rowActions} disabled={isActing} projectName={row.name} />
                )}
            </div>
        </div>
    );
}

// Per-row "⋯" overflow menu. The trigger is hidden until the row is hovered
// (or the menu is open / focused), so 50 rows read as a clean list. The menu
// itself is position:fixed and anchored to the trigger so it escapes the list's
// `overflow-hidden` clip; it closes on outside-click, Escape, or scroll.
function RowActionsMenu({ actions, disabled, projectName }: {
    actions: RowAction[];
    disabled?: boolean;
    projectName: string;
}) {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    const MENU_W = 184;

    useEffect(() => {
        if (!open) return;
        const close = () => setOpen(false);
        const onPointerDown = (e: MouseEvent) => {
            if (menuRef.current?.contains(e.target as Node) || triggerRef.current?.contains(e.target as Node)) return;
            setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKey);
        // The list scrolls under a fixed-position menu; close rather than let it
        // float away from its row.
        window.addEventListener('scroll', close, true);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKey);
            window.removeEventListener('scroll', close, true);
        };
    }, [open]);

    const toggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!open && triggerRef.current) {
            const r = triggerRef.current.getBoundingClientRect();
            setPos({ top: r.bottom + 4, left: Math.max(8, r.right - MENU_W) });
        }
        setOpen(o => !o);
    };

    return (
        <div className="relative">
            <button
                ref={triggerRef}
                type="button"
                onClick={toggle}
                disabled={disabled}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label={`Actions for ${projectName}`}
                data-testid="project-actions-trigger"
                className={`p-1.5 rounded-md text-fg-tertiary hover:bg-surface hover:text-fg focus-visible:outline-none focus-visible:shadow-bark-focus transition-all disabled:pointer-events-none disabled:opacity-40 ${
                    open ? 'opacity-100 bg-surface' : 'opacity-60 group-hover:opacity-100 focus-visible:opacity-100'
                }`}
            >
                <MoreHorizontal className="w-4 h-4" strokeWidth={1.8} />
            </button>
            {open && pos && (
                <div
                    ref={menuRef}
                    role="menu"
                    onClick={(e) => e.stopPropagation()}
                    style={{ position: 'fixed', top: pos.top, left: pos.left, width: MENU_W }}
                    className="z-50 bg-surface border border-border-default rounded-md shadow-bark-md py-1"
                >
                    {actions.map((a) => (
                        <React.Fragment key={a.key}>
                            {a.separatorBefore && <div className="my-1 h-px bg-divider" aria-hidden="true" />}
                            <button
                                type="button"
                                role="menuitem"
                                onClick={(e) => { e.stopPropagation(); setOpen(false); a.onClick(); }}
                                className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-[13px] text-left transition-colors ${
                                    a.danger
                                        ? 'text-danger hover:bg-danger-bg'
                                        : 'text-fg-secondary hover:bg-bg-warm hover:text-fg'
                                }`}
                            >
                                <span className="flex h-4 w-4 flex-shrink-0 items-center justify-center">{a.icon}</span>
                                <span>{a.label}</span>
                            </button>
                        </React.Fragment>
                    ))}
                </div>
            )}
        </div>
    );
}

function EmptyState({ onNewProject, scope, hasAnyProjects, buildLocked }: {
    onNewProject: (type: 'research' | 'build') => void;
    scope: Scope;
    hasAnyProjects: boolean;
    buildLocked: boolean;
}) {
    return (
        <div className="flex flex-col items-center justify-center py-24 text-center">
            <img
                src="/Data/images/BARK_tree_2_navy.png"
                alt="BARK"
                className="w-12 h-12 object-contain mb-4"
            />
            <p className="font-body text-[17px] text-fg font-semibold mb-1">
                {hasAnyProjects ? 'Nothing matches this view' : 'No projects yet'}
            </p>
            <p className="text-sm text-fg-secondary max-w-sm mb-5">
                {hasAnyProjects
                    ? `No projects in ${SCOPE_LABEL[scope]}. Change filters or start a new one.`
                    : buildLocked
                        ? 'Start your first regulatory research project.'
                        : 'Start your first regulatory research or build project.'}
            </p>
            {/* Two entry points, mirroring the left-nav: each opens an empty
                workspace of that type (project created lazily on first message).
                Research is the primary action; Build is the secondary. Both are
                min-w-matched + centred so the pills are identical, and the icons
                are the SAME SVGs as the left-nav entries (magnifier / beaker). */}
            <div className="flex items-center gap-3">
                <button
                    onClick={() => onNewProject('research')}
                    className="inline-flex min-w-[200px] items-center justify-center gap-2 px-4 py-2 rounded-md bg-accent text-white text-sm font-semibold hover:bg-accent-hover transition-colors"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                            Regulatory research
                </button>
                {!buildLocked && (
                    <button
                        onClick={() => onNewProject('build')}
                        className="inline-flex min-w-[200px] items-center justify-center gap-2 px-4 py-2 rounded-md border border-border-default bg-surface text-fg text-sm font-semibold hover:bg-bg-warm transition-colors"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                        </svg>
                                Build &amp; artifacts
                    </button>
                )}
            </div>
        </div>
    );
}

export default ProjectLibraryView;
